import { useEffect, useRef } from "react"
import { maxTurnsToBeBankrupt } from "@mono/constants"
import {
  addNotification,
  setIdleWarning,
  setState,
} from "@/store/globalSlice"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { useGameTimings } from "@/Hooks/useGameTimings"
import { getRandomRoll } from "@/utils/animationHelpers"
import { getUserCreds, socket } from "@/utils/utils"

// How often the idle clock is checked. Faster than the once-a-second badge it
// feeds, so the warning disappears promptly when the player finally moves
// rather than lingering for most of a second.
const TICK_INTERVAL = 250
const AUTO_END_NOTIFICATION_DURATION = 2500

// Every kind of input that counts as "the player is still there". Registered
// in the capture phase on window so a modal that stops propagation on its own
// clicks still keeps the turn alive.
//
// Deliberately no pointermove: a resting hand nudges the mouse enough to hold a
// turn open forever, which would defeat the point. It takes an actual click,
// key, scroll or touch — clicking empty board space is enough.
const ACTIVITY_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
] as const

// Plays the active player's turn for them once they stop responding, after
// warning them first. Two stages, both measured from their last input: silence
// for autoEndTurnIdleTime puts the countdown on screen, and silence through the
// following autoEndTurnTime takes the move. Any input at any point resets the
// whole thing, so the countdown is only ever seen by someone who really has
// stopped playing.
//
// Which move gets taken depends on what the turn is actually waiting for: an
// unrolled turn is rolled, an already-rolled one is ended. Rolling wins when
// both are legal, since ending a turn that still owes a roll would silently
// throw the move away. Each auto-move re-arms the clock from scratch, so an
// idle player rolls, then gets a fresh countdown before their turn ends.
//
// A player sitting on a negative balance is covered too, with only the end turn
// on offer — rolling isn't a legal move while underwater — and never on the last
// of their grace turns, since ending that one is what removes them. See
// `isInDebt` / `isFinalDebtTurn` below.
//
// This lives on the client because it is the only place that can see input at
// all — the server has no way to tell an idle player from a thinking one. It is
// therefore advisory, not authoritative: the server's own turn timer still runs
// underneath and remains the real cutoff. Nothing here can act for anyone else;
// it only ever emits for the player sitting at this screen.
export const useIdleAutoAction = () => {
  const dispatch = useAppDispatch()

  // A quick game shortens both stages along with every other clock, so an idle
  // player is stepped over sooner there.
  const { autoEndTurnIdleTime, autoEndTurnTime, turnTotalTime } = useGameTimings()

  const gameId = useAppSelector((state) => state.global.myGame?.id)
  const situation = useAppSelector((state) => state.global.myGame?.situation)
  const modalData = useAppSelector((state) => state.global.modalData)
  const modal = useAppSelector((state) => state.global.modal)
  const pendingDecision = useAppSelector((state) => state.global.pendingDecision)
  const viewedTradeId = useAppSelector((state) => state.global.viewedTradeId)
  const tempTradeData = useAppSelector((state) => state.global.tempTradeData)
  const disableEndTurnLocally = useAppSelector(
    (state) => state.global.disableEndTurnLocally
  )
  const diceRolling = useAppSelector((state) => state.global.diceRolling)
  const pieceMovementAnimationRunning = useAppSelector(
    (state) => state.global.pieceMovementAnimationRunning
  )
  const houseBuildMode = useAppSelector((state) => state.global.houseBuildMode)

  const myEmail = getUserCreds().email
  const myOrder = situation?.players.find((player) => player.id === myEmail)?.order
  const activePlayer = situation?.players.find(
    (player) => player.id === situation?.turn
  )

  // Any open modal pauses the clock. Those flows (auction, trade, card
  // decisions) carry their own timers, and letting two countdowns race would
  // mean a player deliberating inside a modal could have the turn pulled out
  // from under them. Scanned generically so a modal added later is covered
  // without having to remember this file.
  //
  // `bankrupt` is the one exception, and deliberately so: it is not a decision
  // with a clock of its own, it is the permanent view a player sits behind for
  // as long as their balance is negative. Counting it as "a human is busy" is
  // what let someone go under and then walk away, stalling the whole table
  // until the server's turn timer ran out on every one of their turns.
  const isAnyModalOpen =
    !!modal ||
    Object.entries(modalData || {}).some(
      ([name, item]) =>
        name !== "bankrupt" &&
        !!item &&
        typeof item === "object" &&
        (item as { visible?: boolean }).visible
    )

  // The trade player chooser is opened straight from BankruptModal's Trade
  // button and isn't a `modalData` entry, so the scan above can't see it. Picking
  // an opponent is exactly the kind of deliberation that must hold the turn.
  const isChoosingTradePlayer = tempTradeData?.status === "choosing-player"

  // A negative balance narrows what can be taken automatically: GameButtons
  // hides the Roll button outright while underwater, so the only move left is
  // the End turn that BankruptModal offers.
  const isInDebt = (activePlayer?.money ?? 0) < 0

  // Ending a turn in the red spends one of the `maxTurnsToBeBankrupt` grace
  // turns, and ending the last one is what removes the player from the game.
  // BankruptModal hides its own End turn button there for that reason, and the
  // idle clock has to respect the same line — being away from the keyboard
  // shouldn't hand out an elimination that a trade could still have undone. The
  // server's turn timer stays the real cutoff on that final turn.
  const isFinalDebtTurn =
    isInDebt &&
    (activePlayer?.turnsBeingBankrupt ?? 0) >= maxTurnsToBeBankrupt - 1

  // Shared by both moves: it has to be our turn, the board has to be settled,
  // and nothing may be waiting on a human anywhere.
  const isTurnIdleTrackable =
    !!gameId &&
    !!situation &&
    !situation.gameOver &&
    situation.turn === myEmail &&
    myOrder !== undefined &&
    !isFinalDebtTurn &&
    situation.activeAuctionPropertyOrder == null &&
    !pendingDecision &&
    !isAnyModalOpen &&
    !isChoosingTradePlayer &&
    !viewedTradeId &&
    !houseBuildMode &&
    !diceRolling &&
    !pieceMovementAnimationRunning

  // Mirrors the Roll button's own gate in GameButtons: hidden entirely on a
  // negative balance, and a piece still travelling means the previous roll
  // hasn't finished resolving.
  const canRoll =
    isTurnIdleTrackable &&
    !isInDebt &&
    situation?.rollEnabled !== false &&
    !situation?.events.find((event) => event.type === "piece-movement")

  // Mirrors the End Turn button's gate, including the local lock that is held
  // between sending an end-turn and the server's new state arriving. In debt the
  // button being mirrored is BankruptModal's rather than GameButtons' — that one
  // is offered whatever `endTurnEnabled` says, and has to be, since a player who
  // can't roll would otherwise never see the flag turn true.
  const canEndTurn =
    isTurnIdleTrackable &&
    (isInDebt || situation?.endTurnEnabled === true) &&
    !disableEndTurnLocally

  const idleAction: IdleAction | null = canRoll
    ? "roll"
    : canEndTurn
    ? "end-turn"
    : null

  // Read inside the interval, which is deliberately not rebuilt on every
  // render — refs keep it looking at current values without restarting the
  // clock (and resetting the player's idle time) each time the store changes.
  const lastActivityRef = useRef(Date.now())
  const shownRef = useRef<{ secondsLeft: number; action: IdleAction } | null>(null)
  const gameIdRef = useRef(gameId)
  const myOrderRef = useRef(myOrder)
  const actionRef = useRef(idleAction)
  const inJailRef = useRef(activePlayer?.inJail)
  gameIdRef.current = gameId
  myOrderRef.current = myOrder
  actionRef.current = idleAction
  inJailRef.current = activePlayer?.inJail

  // Draws our own badge and, in the same breath, tells the room — the other
  // players' badges are just this value relayed, so they can never disagree
  // with what the idle player is looking at. Only fires on an actual change,
  // which keeps this to roughly one message a second while someone is idle and
  // none at all the rest of the time.
  const showWarning = (
    warning: { secondsLeft: number; action: IdleAction } | null
  ) => {
    const shown = shownRef.current
    if (
      shown?.secondsLeft === warning?.secondsLeft &&
      shown?.action === warning?.action
    ) {
      return
    }
    shownRef.current = warning
    dispatch(
      setIdleWarning(
        warning && myOrderRef.current !== undefined
          ? { playerOrder: myOrderRef.current as PlayerOrder, ...warning }
          : null
      )
    )
    if (gameIdRef.current) {
      socket.emit("game.idle-warning", gameIdRef.current, warning)
    }
  }

  // Listeners stay mounted for the whole game rather than only while a move is
  // pending: input that arrives just before the turn becomes trackable still
  // counts, so the player gets a full idle period from their last interaction.
  useEffect(() => {
    const onActivity = () => {
      lastActivityRef.current = Date.now()
      // Clear the warning immediately instead of waiting for the next tick —
      // the badge should vanish the instant the player touches anything.
      if (shownRef.current !== null) showWarning(null)
    }

    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, onActivity, {
        capture: true,
        passive: true,
      })
    )
    return () =>
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, onActivity, { capture: true })
      )
  }, [])

  // Keyed on the pending move, so finishing one (roll -> end turn) tears the
  // clock down and starts the next from a full idle period rather than letting
  // leftover silence run straight into a second auto-move.
  useEffect(() => {
    if (!idleAction) {
      lastActivityRef.current = Date.now()
      showWarning(null)
      return
    }

    lastActivityRef.current = Date.now()

    const interval = setInterval(() => {
      const action = actionRef.current
      if (!action) return

      const idleFor = (Date.now() - lastActivityRef.current) / 1000
      if (idleFor < autoEndTurnIdleTime) {
        showWarning(null)
        return
      }

      const secondsLeft = Math.ceil(
        autoEndTurnIdleTime + autoEndTurnTime - idleFor
      )
      if (secondsLeft > 0) {
        showWarning({
          secondsLeft: Math.min(secondsLeft, autoEndTurnTime),
          action,
        })
        return
      }

      // Idle through both stages — take the move. Re-arm first so a slow round
      // trip can't let the next tick fire the same thing twice.
      showWarning(null)
      lastActivityRef.current = Date.now()

      if (action === "roll") {
        // Same emit the Roll button makes, minus the dice-input cheat field:
        // an automatic roll is always a plain random one.
        dispatch(setState({ diceRolling: true }))
        if (inJailRef.current) {
          socket.emit("game.diceRoll", gameIdRef.current || "", getRandomRoll(), true)
        } else {
          socket.emit("game.diceRoll", gameIdRef.current || "", getRandomRoll())
        }
        return
      }

      // Mirrors the manual End Turn button, including the local lock that stops
      // a second end-turn being sent before the server's new state lands.
      dispatch(
        setState({ timerValue: turnTotalTime, disableEndTurnLocally: true })
      )
      socket.emit("game.endTurn", gameIdRef.current || "")
      if (myOrderRef.current !== undefined) {
        dispatch(
          addNotification({
            type: "turn-auto-ended",
            playerOrder: myOrderRef.current as PlayerOrder,
            duration: AUTO_END_NOTIFICATION_DURATION,
          })
        )
      }
    }, TICK_INTERVAL)

    return () => clearInterval(interval)
    // The timings are one of two shared objects, so this only re-arms if the
    // game on screen actually changes pace — i.e. once, when it first loads.
  }, [idleAction, dispatch, autoEndTurnIdleTime, autoEndTurnTime, turnTotalTime])

  // A countdown must never outlive the screen it belongs to.
  useEffect(() => () => showWarning(null), [])
}
